import React, { useCallback, useState } from 'react';
import { Preceptor } from '../shared/VolunteerApplicationServiceTypes';
import { useTranslation } from 'react-i18next';
import { PreceptorTableColumns } from '../shared/Columns';
import { Button, Table } from 'antd';
import { ADD_MORE_PRECEPTORS, ASSIGN_MENTOR, GROUPS } from '../shared/Routes';
import { useEditGroup } from '../pages/nps/EditGroupPage/useEditGroup';
import {
	PreworkGroupTabs,
	MAX_PRECEPTORS_FOR_GROUP,
} from '../shared/Constants';
import { useGroupId } from '../hooks/useGroupId';
import useUserSession from '../hooks/useUserSession';
import { hasPrivilege } from '../shared/Utils';
import { PrivilegeCodes } from '../shared/RoleConstants';

export const GroupPreceptorsTable = ({
	groupPreceptors,
	isEditGroupTable = false,
	mentor,
}: {
	groupPreceptors: Array<Preceptor> | undefined;
	isEditGroupTable?: boolean;
	mentor?: Preceptor;
}) => {
	const { t } = useTranslation();
	const { navigateWithGroupId, groupId, navigateWithGroupTab } = useGroupId();
	const { updateGroup, onConfirmDeleteGroup } = useEditGroup();
	const { loggedInUserPrivileges } = useUserSession();
	const groupPreceptorIds =
		groupPreceptors && groupPreceptors.map((preceptor) => preceptor.id);
	const [selectedGroupPreceptors, setSelectedGroupPreceptors] = useState<
		React.Key[]
	>([]);

	const onSelectChange = (newSelectedPreceptorKeys: React.Key[]) => {
		setSelectedGroupPreceptors(newSelectedPreceptorKeys);
	};

	const groupPreceptorSelection = {
		selectedGroupPreceptors,
		onChange: onSelectChange,
	};
	const hasSelected = selectedGroupPreceptors.length > 0;
	const disableAddMore =
		groupPreceptorIds && groupPreceptorIds.length === MAX_PRECEPTORS_FOR_GROUP;

	const handleRemoveClick = useCallback(() => {
		const preceptorIdsToBeUpdated =
			groupPreceptorIds &&
			groupPreceptorIds.filter((id) => !selectedGroupPreceptors.includes(id));
		if (preceptorIdsToBeUpdated) {
			updateGroup(preceptorIdsToBeUpdated, 'areYouSureToRemoveGroup', false);
		}
	}, [groupPreceptorIds, updateGroup, selectedGroupPreceptors]);

	const handleAddMoreClick = useCallback(() => {
		navigateWithGroupId(ADD_MORE_PRECEPTORS, groupId);
	}, [navigateWithGroupId, groupId]);

	const handleAssignMentorClick = useCallback(() => {
		navigateWithGroupId(ASSIGN_MENTOR, groupId);
	}, [navigateWithGroupId, groupId]);

	const handleBackClick = useCallback(() => {
		navigateWithGroupTab(GROUPS, PreworkGroupTabs.GROUPS_LIST_VIEW);
	}, [navigateWithGroupTab]);

	const handleRemoveGroupClick = useCallback(() => {
		onConfirmDeleteGroup([groupId]);
	}, [groupId, onConfirmDeleteGroup]);
	const columns = PreceptorTableColumns(true);

	const showAssignMentor = hasPrivilege(
		PrivilegeCodes.ASSIGN_MENTOR,
		loggedInUserPrivileges
	);

	const showRemoveGroup = hasPrivilege(
		PrivilegeCodes.DELETE_PRECEPTOR_GROUPS,
		loggedInUserPrivileges
	);

	const showUpdatePreceptorGroup = hasPrivilege(
		PrivilegeCodes.UPDATE_PRECEPTOR_GROUP,
		loggedInUserPrivileges
	);

	const showPreceptors = hasPrivilege(
		PrivilegeCodes.GET_PRECEPTORS,
		loggedInUserPrivileges
	);

	if (
		groupPreceptors &&
		groupPreceptors.length &&
		groupPreceptors.length === 0
	) {
		return <div>{t('noDataFound')}</div>;
	}

	return (
		<>
			{groupPreceptors && (
				<>
					<h4>{t('groupMembers')}</h4>
					<Table
						rowSelection={
							isEditGroupTable ? groupPreceptorSelection : undefined
						}
						scroll={{ x: true }}
						dataSource={groupPreceptors}
						pagination={false}
						columns={columns}
					/>
					{!isEditGroupTable && mentor && (
						<div className="mt-5">
							<h4>{t('mentor')}</h4>
							<Table
								scroll={{ x: true }}
								dataSource={[mentor]}
								pagination={false}
								columns={columns}
								rowKey={(record) => {
									return `mentor-row-${record.id}`;
								}}
							/>
						</div>
					)}
					<div className="flex flex-row-reverse items-center gap-4 my-8">
						{isEditGroupTable && showPreceptors && (
							<>
								<Button
									type="primary"
									onClick={handleAddMoreClick}
									disabled={disableAddMore}
								>
									{t('addMore')}
								</Button>
								{showUpdatePreceptorGroup && (
									<Button
										type="primary"
										onClick={handleRemoveClick}
										disabled={!hasSelected || groupPreceptors.length <= 1}
									>
										{selectedGroupPreceptors.length > 1
											? t('removeMembers')
											: t('removeMember')}
									</Button>
								)}
							</>
						)}
						<>
							{!isEditGroupTable && showAssignMentor && (
								<Button type="primary" onClick={handleAssignMentorClick}>
									{t('assignMentor')}
								</Button>
							)}
							{showRemoveGroup && (
								<Button type="primary" onClick={handleRemoveGroupClick}>
									{t('removeGroup')}
								</Button>
							)}
							<Button type="default" onClick={handleBackClick}>
								{t('back')}
							</Button>
						</>
					</div>
				</>
			)}
		</>
	);
};
